import {
  isEligibleForTourUpsell,
  useTouringTreatmentForZuidAndListing,
  useTourUpsellCookie,
} from '@zg-rentals/touring';
import {
  useSatelliteWebLandlordLeadManagementHandler_getScreeningCriteria,
  useSatelliteWebLandlordPropertyHandler_getAddressDetails,
} from '@zg-rentals/ts-satellite-client-react-query';
import { AttachmentGuidelines } from '@zg-rentals/satellite-components';
import Head from 'next/head';
import rentalTourIllustration from '../../images/rental-tour.svg';
import { useRouter } from 'next/router';
import { useContext, useEffect, useRef, useState } from 'react';
import { LeadManagementContextProvider } from '../../contexts/LeadManagementContextProvider';
import Layout from '../../components/Layout';
import { Alert, Banner, Box, Button, Heading, Page, TextButton } from '@zillow/constellation-10';
import { IconChecklistFilled, IconChevronLeftFilled } from '@zillow/constellation-icons';
import useReferringListingAlias from '../../hooks/useReferringListingAlias';
import StringConstants from '../../constants/StringConstants';
import PageHeading from '../../components/PageHeading';
import LeadManagementCards from '../../components/LeadManagementCards';
import LeadManagementTable from '../../components/LeadManagementTable';
import LeadManagementTableSkeleton from '../../components/LeadManagementTable/Skeleton';
import LeadManagementCardsSkeleton from '../../components/LeadManagementCards/Skeleton';
import useDevices from '../../hooks/useDevices';
import {
  SatelliteConversationFolderTypeV2,
  SatelliteConversationReadRepliedStatusTypeV2,
  SatelliteConversationRenterProgressStatusTypeV2,
} from '@zg-rentals/ts-satellite-client';
import useLeadManagementTableQuery from '../../hooks/useLeadManagementTableQuery';
import useLeadManagementListingAliases from '../../hooks/useLeadManagementListingAliases';
import getFolderFromQueryParam from '../../utils/getFolderFromQueryParam';
import getTimezoneData from '../../utils/dateTime/getTimezoneData';
import getLeadCountHeading from '../../utils/getLeadCountHeading';
import getPropertyDashboardOverviewUrl from '../../utils/getPropertyDashboardOverviewUrl';
import LeadManagementFilters from '../../components/LeadManagementFilters';
import getValueFromEnum from '../../utils/getValueFromEnum';
import useModal from '../../hooks/useModal';
import { emitEditScreeningCriteriaEvent } from '../../constants/clickstreamEvents/screeningCriteria';
import LeadManagementSearchBar from '../../components/LeadManagementSearchBar';
import EmptyState from '../../components/EmptyState';
import LeadManagementListingsCombobox from '../../components/LeadManagementListingsCombobox';
import { AppContext } from '../../contexts/AppContextProvider';
import useQueryParamsNext from '../../hooks/useQueryParamsNext';
import { LEAD_MANAGEMENT_PAGE_VIEW_EVENT_TYPE_ID } from '../../constants/clickstreamEvents/pageView';
import useEmitLeadManagementPageView from '../../hooks/useEmitLeadManagementPageView';
import LeadManagementPageSkeleton from '../../components/LeadManagementPageSkeleton';
import { withRedirectBoundary } from '../../components/RedirectBoundary';
import RenterMessagingModal from '../../components/Modals/RenterMessagingModal';
import TourSchedulingUpsellModal from '../../components/Modals/TourSchedulingUpsellModal';
import RenterScreeningCriteriaModal from '../../components/Modals/RenterScreeningCriteriaModal';
import { ModalType } from '../../types/ModalType';
import { useAuth } from '../../AuthProvider';
import formatListingAddress from '../../utils/formatListingAddress';
import useHasProperties from '../../hooks/useHasProperties';
import { leadManagementHeaderStyles, leadManagementTitleRowStyles } from '../../styles/leadManagementStyles';
import {
  CLICK_ON_SHARE_FEEDBACK_EVENT_TYPE_ID,
  emitClickOnShareFeedbackEvent,
  emitSelectPropertyEvent,
} from '../../constants/clickstreamEvents/leadManagementTable';

const FEEDBACK_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSfIBc53DQBNv3MW80eeppNcvZoBjXtXHEFUJXFOVlgOWspjkQ/viewform';

export default withRedirectBoundary(
  () => {
    const router = useRouter();

    const { user } = useAuth();
    const listingAliases = useLeadManagementListingAliases();
    // Ref updates synchronously so rapid clicks don't re-emit against a stale URL snapshot.
    const lastEmittedAliasesRef = useRef<Array<string>>(listingAliases);
    useEffect(() => {
      lastEmittedAliasesRef.current = listingAliases;
    }, [listingAliases]);
    const [conversationIdToFilterBy, setConversationIdToFilterBy] = useState<string | null>(null);
    const { paths } = useContext(AppContext);
    const { isMobileView, isWindowWidthSet } = useDevices();
    const { removeAllQueryParams, updateQueryParams } = useQueryParamsNext();
    const { activeModal, clearModal, setActiveModal } = useModal();
    const { query } = router;
    const {
      conversationId,
      folder,
      listingAlias,
      messagingConversationId,
      minCriteriaMatches,
      readRepliedStatus,
      renterProgressStatus,
    } = query;
    const { clientTimeZone } = getTimezoneData();

    // Empty when 0 or >1 selected so per-listing APIs (tours, criteria, modals) self-disable.
    const singleListingAlias = listingAliases.length === 1 ? listingAliases[0] : '';

    const queryParamValues = {
      conversationId: conversationId?.toString() || '',
      folder: getFolderFromQueryParam(folder?.toString() || SatelliteConversationFolderTypeV2.INBOX),
      listingAlias: singleListingAlias,
      messagingConversationId: messagingConversationId?.toString() || '',
      minCriteriaMatches: Number(minCriteriaMatches) || null,
      readRepliedStatus:
        getValueFromEnum(readRepliedStatus?.toString(), SatelliteConversationReadRepliedStatusTypeV2) ||
        SatelliteConversationReadRepliedStatusTypeV2.SHOW_ALL,
      renterProgressStatus:
        getValueFromEnum(renterProgressStatus?.toString(), SatelliteConversationRenterProgressStatusTypeV2) ||
        SatelliteConversationRenterProgressStatusTypeV2.SHOW_ALL,
    };

    const { hasUpsellCookieSetForListing } = useTourUpsellCookie(queryParamValues.listingAlias);
    const hasListingAlias = Boolean(listingAlias);

    useEmitLeadManagementPageView(user, queryParamValues.listingAlias);

    const {
      leads,
      totalLeadCount,
      isLoading: isLoadingLeadsData,
      isError: hasErrorLoadingLeadsData,
    } = useLeadManagementTableQuery(
      {
        clientTimeZone,
        conversationId: conversationIdToFilterBy,
        listingAliases,
        folder: queryParamValues.folder,
        minCriteriaMatches: queryParamValues.minCriteriaMatches,
        readRepliedStatus: queryParamValues.readRepliedStatus,
        renterProgressStatus: queryParamValues.renterProgressStatus,
      },
      { enabled: router.isReady },
    );

    const shouldGetTouringTreatment =
      !hasUpsellCookieSetForListing && !queryParamValues.conversationId && !!queryParamValues.listingAlias;
    const { data: touringTreatmentData } = useTouringTreatmentForZuidAndListing(
      {
        listingAlias: queryParamValues.listingAlias,
      },
      {
        enabled: shouldGetTouringTreatment,
        errorDetails: 'LeadManagementPage - Error fetching touring treatment',
      },
    );

    // Listing-level details are per-row to support multi-listing; derive from the first lead.
    const firstLeadListingDetails = leads[0]?.listingDetails;
    const heading = getLeadCountHeading({
      totalLeadCount,
      isLoading: isLoadingLeadsData,
      hasError: hasErrorLoadingLeadsData,
    });
    const hasNoLeadsToDisplay = !isLoadingLeadsData && !hasErrorLoadingLeadsData && leads?.length === 0;
    const listingAddress = formatListingAddress(firstLeadListingDetails?.address);
    // Empty alias (0 or >1 listings selected) falls back to the properties index inside the helper.
    const propertyDashboardOverviewUrl = getPropertyDashboardOverviewUrl(
      paths.myPropertiesPageWithBasePath,
      singleListingAlias,
    );
    const { hasProperties, isLoading: isLoadingProperties } = useHasProperties();
    const { data: criteriaData } = useSatelliteWebLandlordLeadManagementHandler_getScreeningCriteria(
      { listingAlias: queryParamValues.listingAlias },
      {
        enabled: !!queryParamValues.listingAlias,
        meta: {
          tags: ['satellite:propertyScreeningCriteria'],
          context: {
            api: 'getScreeningCriteria',
            details: 'LeadManagementPage - Error fetching criteria',
          },
        },
      },
    );
    const isScreeningCriteriaEnabled = criteriaData?.isEnabled ?? true;

    const hasFiltersApplied = !!folder || !!minCriteriaMatches || !!readRepliedStatus || !!renterProgressStatus;
    const hasErrorWithNoData = hasErrorLoadingLeadsData && leads.length === 0;
    const shouldDisplayAlertMessage =
      hasErrorWithNoData || hasNoLeadsToDisplay || (hasNoLeadsToDisplay && hasFiltersApplied);
    // The "Back to property" link reflects the property the user navigated from (the referrer), not
    // the current selection or first lead — so it stays correct across multi-listing filtering. The
    // address is resolved from the referring alias directly, independent of which leads are loaded.
    const referringListingAlias = useReferringListingAlias();
    const { data: referringPropertyAddressData } = useSatelliteWebLandlordPropertyHandler_getAddressDetails(
      { listingAlias: referringListingAlias ?? '' },
      {
        enabled: !!referringListingAlias,
        meta: {
          context: {
            api: 'getAddressDetails',
            details: 'LeadManagementPage - Error fetching referring property address for back link',
          },
        },
      },
    );
    const backToPropertyUrl = getPropertyDashboardOverviewUrl(
      paths.myPropertiesPageWithBasePath,
      referringListingAlias ?? '',
    );
    const backToPropertyAddress = referringPropertyAddressData?.displayAddress ?? '';

    const shouldShowTourUpsellModal =
      shouldGetTouringTreatment &&
      isEligibleForTourUpsell(touringTreatmentData) &&
      !isLoadingLeadsData &&
      leads &&
      leads.length > 0;

    useEffect(() => {
      if (shouldShowTourUpsellModal) {
        setActiveModal(ModalType.TourSchedulingUpsellModal);
      }
    }, [shouldShowTourUpsellModal]);

    if (!hasListingAlias && (!router.isReady || isLoadingProperties)) {
      return null;
    }

    if (!hasListingAlias && !hasProperties) {
      return (
        <Layout>
          <Head>
            <title>{StringConstants.LEAD_MANAGEMENT_PAGE_TITLE}</title>
          </Head>
          <EmptyState
            illustration={rentalTourIllustration}
            heading={StringConstants.LEADS_EMPTY_STATE_HEADING}
            body={StringConstants.LEADS_NO_PROPERTIES_BODY}
            css={{ justifyContent: 'center', flex: 1, minHeight: 'calc(100vh - 120px)', paddingInline: 'default' }}
            cta={
              <Button tone="brand" emphasis="filled" asChild>
                <a href={paths.myPropertiesPageWithBasePath}>Add property</a>
              </Button>
            }
          />
        </Layout>
      );
    }

    const handleEditScreeningCriteriaClick = () => {
      emitEditScreeningCriteriaEvent(user, { listingAlias: queryParamValues.listingAlias });
      setActiveModal(ModalType.RenterScreeningCriteriaModal);
    };

    const handleClearFilters = () => {
      removeAllQueryParams({
        listingAlias: listingAlias?.toString() ?? '',
        shouldReplaceHistory: true,
      });
      setConversationIdToFilterBy(null);
    };

    const handleListingAliasesChange = (aliases: Array<string>) => {
      const addedAliases = aliases.filter((alias) => !lastEmittedAliasesRef.current.includes(alias));
      lastEmittedAliasesRef.current = aliases;
      for (const addedAlias of addedAliases) {
        emitSelectPropertyEvent(user, { listingAlias: addedAlias });
      }
      updateQueryParams({
        queryParamValues: { listingAlias: aliases.length > 0 ? aliases.join(',') : null },
        pathname: '/lead-management',
      });
    };

    return (
      <Layout>
        <Head>
          <title>{StringConstants.LEAD_MANAGEMENT_PAGE_TITLE}</title>
        </Head>
        {activeModal === ModalType.RenterScreeningCriteriaModal && (
          <RenterScreeningCriteriaModal
            isOpen={true}
            onOpenChange={(open) => !open && clearModal()}
            listingAlias={queryParamValues.listingAlias}
          />
        )}
        <LeadManagementContextProvider
          value={{
            clientTimeZone,
            conversationId: queryParamValues.conversationId,
            conversationIdToFilterBy,
            folder: queryParamValues.folder,
            isArchivedFolder: queryParamValues.folder === SatelliteConversationFolderTypeV2.ARCHIVED,
            isReportedFolder: queryParamValues.folder === SatelliteConversationFolderTypeV2.SPAM,
            listingAlias: queryParamValues.listingAlias,
            listingAliases,
            messagingConversationId: queryParamValues.messagingConversationId,
            minCriteriaMatches: queryParamValues.minCriteriaMatches,
            readRepliedStatus: queryParamValues.readRepliedStatus,
            renterProgressStatus: queryParamValues.renterProgressStatus,
          }}
        >
          {shouldShowTourUpsellModal && (
            <TourSchedulingUpsellModal
              modalHeaderText={listingAddress}
              listingAlias={queryParamValues?.listingAlias || ''}
            />
          )}

          {activeModal === ModalType.AttachmentModal && <AttachmentGuidelines onClose={clearModal} />}

          {queryParamValues.messagingConversationId && (
            <RenterMessagingModal conversationId={queryParamValues.messagingConversationId} />
          )}
          <Banner
            tone="info"
            actionButton={
              <TextButton onImpact asChild>
                <a
                  data-eventid={CLICK_ON_SHARE_FEEDBACK_EVENT_TYPE_ID}
                  href={FEEDBACK_FORM_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => emitClickOnShareFeedbackEvent(user, { listingAlias: queryParamValues.listingAlias })}
                >
                  Share feedback
                </a>
              </TextButton>
            }
            role="status"
          >
            Help inform future improvements to this page
          </Banner>

          <Page.Content
            fluid
            data-eventid={LEAD_MANAGEMENT_PAGE_VIEW_EVENT_TYPE_ID}
            data-eventdescription="Page view"
            css={{ paddingInline: 'loose' }}
          >
            {referringListingAlias && (
              <TextButton asChild icon={<IconChevronLeftFilled />}>
                <a href={backToPropertyUrl}>
                  Back to {backToPropertyAddress ? backToPropertyAddress : 'property dashboard'}
                </a>
              </TextButton>
            )}
            <Box css={leadManagementTitleRowStyles}>
              <PageHeading heading="Leads" />
              {isScreeningCriteriaEnabled && (
                <Button
                  icon={<IconChecklistFilled />}
                  onClick={handleEditScreeningCriteriaClick}
                  disabled={isLoadingProperties || !hasProperties}
                  css={{ flexShrink: 0 }}
                >
                  Manage screening criteria
                </Button>
              )}
            </Box>

            {isMobileView && (
              <LeadManagementListingsCombobox
                selectedListingAliases={listingAliases}
                onChange={handleListingAliasesChange}
                fullWidth
              />
            )}

            <Box css={leadManagementHeaderStyles}>
              <Heading level={2} css={{ flexShrink: 0, textStyle: { xlDown: 'h6', xl: 'h4' }, marginRight: 'auto' }}>
                {heading}
              </Heading>
              <LeadManagementSearchBar
                isDisabled={isLoadingLeadsData}
                setConversationIdToFilterBy={setConversationIdToFilterBy}
              />
              {!isMobileView && (
                <LeadManagementListingsCombobox
                  selectedListingAliases={listingAliases}
                  onChange={handleListingAliasesChange}
                />
              )}
              <LeadManagementFilters />
            </Box>
            {isLoadingLeadsData && !isWindowWidthSet ? (
              <>
                <Box css={{ xlDown: { display: 'none' } }}>
                  <LeadManagementTableSkeleton />
                </Box>

                <Box css={{ xl: { display: 'none' } }}>
                  <LeadManagementCardsSkeleton />
                </Box>
              </>
            ) : (
              <>
                {shouldDisplayAlertMessage ? (
                  <>
                    {hasErrorWithNoData && (
                      <Alert tone="critical" css={{ marginTop: 'tighter', marginBottom: 'default' }}>
                        {StringConstants.GENERIC_ERROR}
                      </Alert>
                    )}

                    {hasNoLeadsToDisplay && !hasFiltersApplied && (
                      <EmptyState
                        illustration={rentalTourIllustration}
                        heading={StringConstants.LEADS_EMPTY_STATE_HEADING}
                        body={StringConstants.LEADS_EMPTY_STATE_BODY}
                        bodyMaxWidth="357px"
                        withCard
                        css={{ marginTop: 'tighter' }}
                        cta={
                          <Button tone="brand" emphasis="filled" asChild>
                            <a href={propertyDashboardOverviewUrl}>Review your listing</a>
                          </Button>
                        }
                      />
                    )}

                    {hasNoLeadsToDisplay && hasFiltersApplied && (
                      <EmptyState
                        illustration={rentalTourIllustration}
                        heading="No leads match the filters applied"
                        withCard
                        css={{ marginTop: 'tighter' }}
                        cta={
                          <Button tone="brand" emphasis="filled" onClick={handleClearFilters}>
                            Reset filters
                          </Button>
                        }
                      />
                    )}
                  </>
                ) : (
                  <>{isMobileView ? <LeadManagementCards /> : <LeadManagementTable />}</>
                )}
              </>
            )}
          </Page.Content>
        </LeadManagementContextProvider>
      </Layout>
    );
  },
  {
    loading: <LeadManagementPageSkeleton />,
  },
);
